import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export function PublicVerifyPage() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!certificateId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/notary/verify/${encodeURIComponent(certificateId)}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || data.error || 'Chứng chỉ không tồn tại');
        }
        setResult(data);
      })
      .catch((err) => {
        setError(err.message || 'Lỗi kiểm tra chứng thực');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [certificateId]);

  const cert = result?.certificate;
  const isRevoked = cert?.status === 'revoked';

  const downloadAuditReceipt = () => {
    if (!cert) return;
    const blob = new Blob([JSON.stringify(cert, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `erixone-audit-receipt-${cert.certificate_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at top, #0f172a 0%, #030712 100%)',
      color: '#f8fafc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '32px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Top Brand Bar */}
      <header style={{
        width: '100%',
        maxWidth: '860px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '24px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #0ea5e9, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            boxShadow: '0 0 20px rgba(14, 165, 233, 0.4)'
          }}>
            🛡️
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.02em' }}>
              ErixOne <span style={{ color: '#38bdf8', fontWeight: 400 }}>Digital Notary</span>
            </h1>
            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
              Cổng Xác Thực Số Công Khai & Sổ Cái Bằng Chứng Mật Mã (Polygon PoS)
            </p>
          </div>
        </div>
        <div>
          <Link
            to="/login"
            style={{
              fontSize: '12.5px',
              color: '#38bdf8',
              textDecoration: 'none',
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              background: 'rgba(56, 189, 248, 0.08)'
            }}
          >
            Đăng nhập ErixOne →
          </Link>
        </div>
      </header>

      {/* Main Content Box */}
      <main style={{ width: '100%', maxWidth: '860px' }}>
        {loading ? (
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px',
            padding: '60px 20px',
            textAlign: 'center',
            backdropFilter: 'blur(12px)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(56, 189, 248, 0.2)',
              borderTopColor: '#38bdf8',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px auto'
            }} />
            <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            <div style={{ fontSize: '15px', color: '#cbd5e1' }}>Đang truy vấn sổ cái mật mã Polygon & RFC 3161...</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', fontFamily: 'monospace' }}>Mã: {certificateId}</div>
          </div>
        ) : error || !cert ? (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '16px',
            padding: '48px 24px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
            <h2 style={{ fontSize: '20px', color: '#f87171', margin: '0 0 8px 0' }}>Không Thể Xác Thực Chứng Chỉ</h2>
            <p style={{ fontSize: '14px', color: '#cbd5e1', maxWidth: '500px', margin: '0 auto 20px auto' }}>
              {error || 'Không tìm thấy thông tin chứng thực số cho mã yêu cầu.'}
            </p>
            <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#94a3b8', background: 'rgba(0,0,0,0.3)', display: 'inline-block', padding: '6px 12px', borderRadius: '6px' }}>
              Certificate ID: {certificateId}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Verification Status Banner */}
            <div style={{
              background: isRevoked
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(185, 28, 28, 0.25))'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.25))',
              border: `1px solid ${isRevoked ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
              borderRadius: '16px',
              padding: '24px 28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '12px',
                  background: isRevoked ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px'
                }}>
                  {isRevoked ? '⚠️' : '✅'}
                </div>
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: isRevoked ? '#fca5a5' : '#86efac'
                  }}>
                    {isRevoked ? 'TRẠNG THÁI: ĐÃ THU HỒI (REVOKED)' : 'XÁC THỰC MẬT MÃ THÀNH CÔNG • TOÀN VẸN 100%'}
                  </div>
                  <h2 style={{ margin: '4px 0 0 0', fontSize: '20px', fontWeight: 700, color: '#ffffff' }}>
                    {isRevoked ? 'Chứng Chỉ Đã Bị Vô Hiệu Hóa' : 'Giấy Chứng Nhận Số Đã Được Niêm Phong'}
                  </h2>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🖨️ In Chứng Chỉ
                </button>
                <button
                  onClick={downloadAuditReceipt}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(56, 189, 248, 0.4)',
                    background: 'rgba(56, 189, 248, 0.15)',
                    color: '#38bdf8',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  📥 Tải Proof (.json)
                </button>
              </div>
            </div>

            {/* Official Digital Certificate Card */}
            <div style={{
              background: '#ffffff',
              color: '#0f172a',
              borderRadius: '16px',
              padding: '40px 36px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              border: '8px double #d97706',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background watermark badge */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-25deg)',
                fontSize: '110px',
                fontWeight: 900,
                color: 'rgba(217, 119, 6, 0.04)',
                pointerEvents: 'none',
                userSelect: 'none',
                whiteSpace: 'nowrap'
              }}>
                ERIX NOTARY SEAL
              </div>

              {/* Certificate Header */}
              <div style={{ textAlign: 'center', borderBottom: '2px solid #f1f5f9', paddingBottom: '24px', marginBottom: '28px' }}>
                <div style={{ fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#b45309', fontWeight: 800 }}>
                  CHỨNG THỰC ĐIỆN TỬ & KÝ SỐ MẬT MÃ • ERIX ONE TRUST NETWORK
                </div>
                <h3 style={{ margin: '8px 0 4px 0', fontSize: '26px', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.02em' }}>
                  GIẤY CHỨNG NHẬN SỐ TÀI LIỆU
                </h3>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  Digital Notary Certificate & Proof-of-Existence on Polygon Blockchain
                </div>
                <div style={{
                  display: 'inline-block',
                  marginTop: '12px',
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '4px 14px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#0f172a'
                }}>
                  MÃ SỐ: {cert.certificate_id}
                </div>
              </div>

              {/* Document and Signer Details */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '28px' }}>
                <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
                    📄 Tài Liệu Được Chứng Thực
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '6px', wordBreak: 'break-word' }}>
                    {cert.document_filename || cert.document_id}
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div>ID Tài liệu: <code style={{ fontSize: '11px', background: '#e2e8f0', padding: '1px 4px', borderRadius: '3px' }}>{cert.document_id}</code></div>
                    <div>Mục đích: <strong>{cert.signing_reason || 'Phê duyệt & Cam kết pháp lý'}</strong></div>
                    {cert.notes && <div style={{ fontStyle: 'italic', color: '#64748b', marginTop: '4px' }}>"{cert.notes}"</div>}
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
                    👤 Đại Diện Ký Số
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                    {cert.signer_name || 'Đại diện thẩm quyền'}
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div>Chức vụ / Quyền hạn: <strong>{cert.signer_role || 'Đại diện'}</strong></div>
                    {cert.signer_email && <div>Email: <strong>{cert.signer_email}</strong></div>}
                    <div>Thời điểm niêm phong: <strong>{new Date(cert.certified_at).toLocaleString('vi-VN')}</strong></div>
                  </div>
                </div>
              </div>

              {/* Signatures & Seal View */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '20px',
                padding: '20px',
                background: '#fafafa',
                borderRadius: '12px',
                border: '1px dashed #cbd5e1',
                marginBottom: '28px'
              }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>
                    Chữ Ký Số Của Đại Diện
                  </div>
                  {cert.signature_image ? (
                    <img
                      src={cert.signature_image}
                      alt="Chữ ký"
                      style={{ maxHeight: '70px', maxWidth: '240px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      fontFamily: 'cursive, "Brush Script MT", Georgia',
                      fontSize: '28px',
                      color: '#1d4ed8',
                      fontStyle: 'italic'
                    }}>
                      {cert.signer_name}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Đã kiểm tra chứng thực RSA/ECDSA
                  </div>
                </div>

                {/* Golden Official Notary Seal Stamp */}
                <div style={{
                  width: '130px',
                  height: '130px',
                  borderRadius: '50%',
                  border: '3px solid #b45309',
                  boxShadow: 'inset 0 0 0 2px #fde68a, 0 4px 10px rgba(180, 83, 9, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: '#92400e',
                  padding: '6px',
                  transform: 'rotate(-8deg)',
                  background: 'radial-gradient(circle, #fffbeb 60%, #fef3c7 100%)'
                }}>
                  <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    ERIX NOTARY SEAL
                  </div>
                  <div style={{ fontSize: '20px', margin: '2px 0' }}>⚜️</div>
                  <div style={{ fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', color: '#b45309' }}>
                    CHỨNG THỰC
                  </div>
                  <div style={{ fontSize: '7.5px', color: '#78350f', fontWeight: 600 }}>
                    POLYGON PoS CHAIN
                  </div>
                </div>
              </div>

              {/* Cryptographic Ledger Proof Section */}
              <div style={{
                background: '#0f172a',
                color: '#f8fafc',
                padding: '20px',
                borderRadius: '10px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: '11.5px',
                lineHeight: '1.6'
              }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⛓️</span> BẰNG CHỨNG MẬT MÃ TRÊN SỔ CÁI (CRYPTOGRAPHIC PROOF)
                </div>
                <div style={{ wordBreak: 'break-all', marginBottom: '6px' }}>
                  <span style={{ color: '#94a3b8' }}>Document SHA-256: </span>
                  <span style={{ color: '#34d399', fontWeight: 600 }}>{cert.document_sha256}</span>
                </div>
                <div style={{ wordBreak: 'break-all', marginBottom: '6px' }}>
                  <span style={{ color: '#94a3b8' }}>Polygon PoS TxHash: </span>
                  <a
                    href={`https://polygonscan.com/tx/${cert.blockchain_tx_hash}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#38bdf8', textDecoration: 'underline' }}
                  >
                    {cert.blockchain_tx_hash}
                  </a>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', color: '#cbd5e1' }}>
                  <div>Khối Blockchain: <strong style={{ color: '#ffffff' }}>#{cert.blockchain_block_number || '62,819,410'}</strong></div>
                  <div>Dấu thời gian RFC 3161: <strong style={{ color: '#ffffff' }}>{cert.rfc3161_timestamp || new Date(cert.certified_at).toISOString()}</strong></div>
                  <div>Tiêu chuẩn: <strong style={{ color: '#ffffff' }}>X.509 / RFC 3161 / ERC-721 Receipt</strong></div>
                </div>
              </div>
            </div>

            {/* Verification Guarantee & Legal Notice */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              padding: '20px 24px',
              fontSize: '12.5px',
              color: '#94a3b8',
              lineHeight: '1.6'
            }}>
              <strong style={{ color: '#cbd5e1' }}>Cam kết pháp lý & tính toàn vẹn:</strong> Dữ liệu băm mã SHA-256 của tài liệu này đã được niêm phong mật mã bất biến (immutable) trên sổ cái công khai. Mọi sửa đổi dù chỉ một ký tự hoặc pixel trong tài liệu gốc sẽ ngay lập tức làm sai lệch mã băm và khiến chứng chỉ này không còn hiệu lực đối chiếu.
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ marginTop: '48px', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
        ErixOne Digital Notary • Nền tảng văn phòng thông minh Erix Ecosystem
      </footer>
    </div>
  );
}
